<p-accordion
  class="bd-ai-accordion flex flex-col gap-3"
  [class.bd-ai-accordion-with-history]="showChatHistoryPanel()"
  data-id="assistant-chat"
  [multiple]="true"
  [value]="openPanels()"
  (valueChange)="onAccordionChange($event)"
  [dt]="aiAssistantAccordionPreset"
  *transloco="let t; scope: 'featureAiAssistant'; prefix: 'featureAiAssistant'"
>
  <!-- Visualization Suggestions Section (optional) -->
  @if (
    showSuggestionsPanel() ||
    visualSuggestions().length > 0 ||
    refreshingVisualSuggestions()
  ) {
    <p-accordion-panel
      class="bd-suggestions-panel"
      value="suggestions"
      data-id="assistant-suggestions-section"
    >
      <bd-ai-accordion-header
        icon="ui-color"
        [title]="t('suggestions.titlev2')"
      >
      </bd-ai-accordion-header>
      <p-accordion-content>
        <bd-ai-assistant-suggestions-v2
          [suggestions]="visualSuggestions()"
          [refreshing]="refreshingVisualSuggestions()"
          (selectedSuggestion)="onVisualSuggestionSelected($event)"
        />
      </p-accordion-content>
    </p-accordion-panel>
  }

  <!-- AI Chat Section -->
  <p-accordion-panel
    class="bd-chat-panel"
    value="chat"
    data-id="assistant-chat-section"
  >
    <bd-ai-accordion-header
      icon="ui-comment"
      [title]="t('chat.titlev2')"
      [answered]="isChatAnswered()"
      [historyOpen]="showChatHistoryPanel()"
      [historyTitle]="showChatHistoryPanel() ? t('history.title') : ''"
    >
      <p-button
        [icon]="startNewChatEnabled() ? 'ui-pen-to-square' : 'ui-broom-wide'"
        [text]="true"
        styleClass="bd-neutral bd-size-small"
        [disabled]="messages().length === 0"
        [pTooltip]="
          messages().length > 0
            ? startNewChatEnabled()
              ? t('chat.startNewTooltip')
              : t('chat.clearTooltip')
            : startNewChatEnabled()
              ? t('chat.startNewTooltipEmpty')
              : t('chat.clearTooltipEmpty')
        "
        tooltipPosition="left"
        [ariaLabel]="
          startNewChatEnabled() ? t('chat.startNewLabel') : t('chat.clearLabel')
        "
        data-id="assistant-clear-button"
        (onClick)="onClearClick($event)"
      ></p-button>
      @if (chatHistoryEnabled()) {
        <p-button
          icon="ui-recent"
          [text]="true"
          styleClass="bd-neutral bd-size-small"
          [disabled]="chatHistoryBusy() && !showChatHistoryPanel()"
          [pTooltip]="
            showChatHistoryPanel()
              ? t('history.closeTooltip')
              : t('history.openTooltip')
          "
          tooltipPosition="bottom"
          [ariaLabel]="
            showChatHistoryPanel()
              ? t('history.closeLabel')
              : t('history.openLabel')
          "
          [pt]="{
            root: {
              'aria-expanded': showChatHistoryPanel(),
            },
          }"
          data-id="assistant-chat-history-toggle"
          (onClick)="onChatHistoryToggle($event)"
        ></p-button>
      }
    </bd-ai-accordion-header>
    <p-accordion-content>
      <div
        class="bd-v2-chat-layout"
        [class.bd-v2-chat-layout-with-history]="showChatHistoryPanel()"
      >
        @if (showChatHistoryPanel()) {
          <bd-ai-chat-history
            [sessions]="chatSessions()"
            [status]="chatSessionsStatus()"
            [hasMore]="chatSessionsHasMore()"
            [searchTerm]="chatSessionsSearchTerm()"
            [sort]="chatSessionsSort()"
            [scope]="chatSessionsScope()"
            [hasFrameContext]="hasFrameContext()"
            [activeSessionId]="activeChatSessionId()"
            [loadingSessionId]="chatSessionLoadingId()"
            [deletingSessionIds]="pendingChatSessionDeletingIds()"
            [renamingSessionId]="chatSessionRenamingId()"
            [disabled]="isProcessing()"
            (sessionSelected)="chatSessionSelected.emit($event)"
            (deleteSelectedSessions)="onChatSessionsDeleteRequested($event)"
            (renameSession)="chatSessionsRenameRequested.emit($event)"
            (loadMoreRequested)="onChatSessionsLoadMoreRequested()"
            (searchRequested)="onChatSessionsSearchRequested($event)"
            (sortChanged)="onChatSessionsSortChanged($event)"
            (scopeChanged)="onChatSessionsScopeChanged($event)"
          />
        }
        <div class="bd-v2-chat-content">
          <!-- Messages -->
          <div
            class="bd-v2-chat-messages flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto overflow-x-hidden bd-small-scrollbar"
            data-id="assistant-messages-viewport"
            #scrollContainer
          >
            @for (turn of turns(); track turn.id) {
              @if (turn.continued) {
                <hr
                  class="bd-chat-step-separator"
                  data-id="assistant-step-separator"
                />
              }
              <div
                class="bd-chat-turn flex flex-col gap-3"
                [class.bd-chat-turn-current]="$last && !turn.continued"
                [attr.data-turn-id]="turn.id"
              >
                @for (message of turn.messages; track message.id) {
                  @let isUser = message.role === 'user';
                  <div
                    class="flex flex-col gap-2"
                    [ngClass]="{ 'bd-right-aligned-bubble': isUser }"
                  >
                    @let attachments = attachmentsByMessageId()[message.id];
                    @if (isUser) {
                      @let navigationPlace =
                        navigationPlacesByMessageId()[message.id];
                      @if (navigationPlace) {
                        <a
                          class="bd-navigation-chip"
                          data-id="assistant-navigation-place"
                          [routerLink]="
                            navigationPlaceCommands(navigationPlace)
                          "
                        >
                          <i [class]="navigationPlaceIcon(navigationPlace)"></i>
                          {{ navigationPlace.label }}
                        </a>
                      }
                    }
                    @if (attachments) {
                      <div
                        class="flex flex-col gap-2"
                        [ngClass]="isUser ? 'items-end' : 'items-start'"
                        data-id="assistant-message-attachments"
                      >
                        @for (attachment of attachments; track $index) {
                          @if (attachment.isImage) {
                            <bd-thumbnail
                              class="shrink-0"
                              [includeFrame]="true"
                              [preview$]="attachment.preview$"
                              [downloadable]="true"
                              [imageName]="attachment.part.filename"
                              data-id="assistant-attachment-thumbnail"
                              (download)="onDownloadAttachment(attachment.part)"
                            />
                          } @else {
                            <bd-document-upload-chip
                              class="shrink-0"
                              [fileName]="
                                attachment.part.filename ??
                                attachment.part.mediaType
                              "
                              [fileType]="
                                fileTypeLabel(attachment.part.mediaType)
                              "
                              [fileSize]="fileSizeLabel(attachment.part.url)"
                              [iconClass]="
                                fileTypeIcon(attachment.part.mediaType)
                              "
                              [submitted]="true"
                              state="uploaded"
                              errorMessage=""
                              data-id="assistant-attachment-chip"
                              (download)="onDownloadAttachment(attachment.part)"
                            />
                          }
                        }
                      </div>
                    }
                    @if (messagesWithTextContent().has(message.id)) {
                      <div
                        class="bd-chat-bubble bd-body-regular"
                        data-id="assistant-chat-bubble"
                        [ngClass]="
                          isUser
                            ? 'bd-chat-bubble-black'
                            : 'bd-chat-bubble-white'
                        "
                      >
                        @for (part of message.parts; track $index) {
                          @switch (part.type) {
                            @case ('text') {
                              @if (isUser) {
                                <div
                                  style="white-space: pre-wrap"
                                  data-id="assistant-user-message-text"
                                >
                                  {{ part.text }}
                                </div>
                              } @else {
                                <quill-view-html
                                  data-id="assistant-response-text"
                                  [content]="part.text | renderMarkdown"
                                  [theme]="'snow'"
                                  [sanitize]="true"
                                  class="bd-body-regular bd-view-only"
                                ></quill-view-html>
                                @if (part.state === 'streaming') {
                                  <span class="typing-cursor">&#9646;</span>
                                }
                              }
                            }
                            @case ('reasoning') {
                              <details>
                                <summary>{{ t('chat.thinking') }}</summary>
                                <div class="bd-body-regular">
                                  {{ part.text }}
                                </div>
                              </details>
                            }
                            @case ('file') {}
                            @case ('step-start') {}
                            @default {}
                          }
                        }
                      </div>
                    }
                  </div>
                  @if (!isUser) {
                    @let navigationLinks =
                      navigationLinksByMessageId()[message.id];
                    @if (navigationLinks) {
                      <div
                        class="flex gap-2 items-center mt-3"
                        data-id="assistant-navigation-links"
                      >
                        @for (navigation of navigationLinks; track $index) {
                          <button
                            class="bd-navigation-chip"
                            data-id="assistant-navigation-link"
                            type="button"
                            (click)="onNavigationLinkClick(navigation)"
                          >
                            <i [class]="navigationLinkIcon(navigation)"></i>
                            {{ navigationLinkLabel(navigation) }}
                          </button>
                        }
                      </div>
                    }
                  }
                  @if (!isUser) {
                    @let connectPrompts =
                      connectPromptsByMessageId()[message.id];
                    @if (connectPrompts) {
                      <div
                        class="flex flex-col gap-2 items-start mt-3"
                        data-id="assistant-mcp-connect-prompts"
                      >
                        @for (
                          prompt of connectPrompts;
                          track prompt.toolCallId
                        ) {
                          <p-button
                            [label]="
                              t('mcp.connectButton', {
                                mcpName: prompt.mcpName,
                              })
                            "
                            icon="ui-link"
                            [styleClass]="'bd-neutral bd-size-small'"
                            data-id="assistant-mcp-connect-button"
                            (onClick)="onConnectMcpClick(prompt)"
                          ></p-button>
                        }
                      </div>
                    }
                  }
                  @if (!isUser) {
                    @let messageInsights = insightsByMessageId()[message.id];
                    @if (messageInsights) {
                      <div
                        class="bd-body-regular mt-3 p-2"
                        data-id="assistant-insight-data"
                      >
                        @for (insight of messageInsights; track $index) {
                          <div class="flex w-full flex-col gap-1">
                            <span class="bd-label-regular">{{
                              insight.name
                            }}</span>
                            <bd-highcharts-container
                              (click)="onInsightChartClick(insight.insight)"
                              (keydown.enter)="
                                onInsightChartClick(insight.insight)
                              "
                              (keydown.space)="
                                onInsightChartClick(insight.insight);
                                $event.preventDefault()
                              "
                              tabindex="0"
                              class="bd-insight-preview"
                              [attr.data-id]="insight.insight.visualizationId"
                              [data]="insight.highchartsData"
                              [previewMode]="true"
                            ></bd-highcharts-container>
                          </div>
                        }
                      </div>
                    }
                  }
                  @if (!isUser) {
                    @let messageOptions = optionsByMessageId()[message.id];
                    @if (messageOptions) {
                      <div
                        class="flex flex-col gap-2 items-start mt-3"
                        data-id="assistant-options"
                      >
                        @for (option of messageOptions; track option) {
                          <button
                            class="bd-chat-bubble bd-chat-bubble-suggestion bd-body-regular bd-suggestion-chip"
                            data-id="assistant-option-button"
                            [disabled]="
                              pendingOptionSelection()?.messageId === message.id
                            "
                            (click)="onOptionClick(message.id, option)"
                          >
                            {{ option }}
                          </button>
                        }
                      </div>
                    }
                  }
                  @if (!isUser && completedAssistantMessageMap()[message.id]) {
                    <bd-ai-toolbar
                      [feedbackState]="feedbackState()[message.id]"
                      (thumbsUp)="onThumbsUp(message)"
                      (thumbsDown)="onThumbsDown(message)"
                    ></bd-ai-toolbar>
                    @let showFeedbackForm =
                      messageIdShowingFeedback() === message.id;
                    @if (showFeedbackForm) {
                      <p-divider layout="horizontal"></p-divider>
                      <bd-assistant-feedback-form
                        [(comment)]="feedbackComment"
                        mode="voluntary"
                        direction="down"
                        (submitted)="onFeedbackSubmitted($event, message)"
                        (dismissed)="onFeedbackDismissed()"
                      ></bd-assistant-feedback-form>
                    }
                    @if (messageIdThankYouShown() === message.id) {
                      <div
                        *transloco="
                          let tFeedback;
                          scope: 'uiComponents';
                          prefix: 'uiComponents.feedback'
                        "
                        data-id="assistant-feedback-thank-you"
                      >
                        <bd-badge
                          [label]="tFeedback('thankYou')"
                          type="success"
                        ></bd-badge>
                      </div>
                    }
                  }
                }
                @if ($last) {
                  <ng-container
                    *ngTemplateOutlet="conversationStatus"
                  ></ng-container>
                }
              </div>
            }
            @if (turns().length === 0) {
              <ng-container
                *ngTemplateOutlet="conversationStatus"
              ></ng-container>
            }
          </div>

          <!-- Transient status of the current turn: rendered inside that turn so
        the reserved scroll space covers it, or standalone before the first
        message (attachment upload). -->
          <ng-template #conversationStatus>
            @if (plan(); as activePlan) {
              <bd-ai-assistant-plan [plan]="activePlan" />
            } @else if (status() !== 'error') {
              @if (visibleThinkingNote(); as note) {
                <output
                  class="bd-chat-bubble-thinking bd-body-regular"
                  aria-live="polite"
                  aria-atomic="true"
                  data-id="assistant-thinking-indicator"
                >
                  <i class="ui-loading"></i>
                  @for (currentNote of [note]; track currentNote) {
                    <span class="bd-thinking-note-text">{{ currentNote }}</span>
                  }
                </output>
              }
            }
            @if (awaitingInstructions()) {
              <output
                class="bd-chat-bubble-thinking bd-body-regular"
                aria-live="polite"
                aria-atomic="true"
                data-id="assistant-awaiting-instructions"
              >
                <i class="ui-modeler-pause"></i>
                <span class="bd-thinking-note-text">{{
                  t('chat.awaitingInstructions')
                }}</span>
              </output>
            }
            @if (isUploadingAttachments()) {
              <output
                class="bd-chat-bubble-thinking bd-body-regular"
                aria-live="polite"
                aria-atomic="true"
                data-id="assistant-uploading-indicator"
              >
                <i class="ui-loading"></i>{{ t('chat.uploadingAttachments') }}
              </output>
            }
            @if (showSoftPrompt()) {
              <div
                class="bd-feedback-prompt-card"
                data-id="assistant-feedback-soft-prompt"
                *transloco="
                  let tFeedbackPrompt;
                  scope: 'uiComponents';
                  prefix: 'uiComponents.feedback'
                "
              >
                <div class="bd-feedback-prompt-card-header">
                  <i class="ui-info"></i>
                  <div class="flex flex-col gap-1">
                    <span class="bd-label-regular">{{
                      tFeedbackPrompt('softPromptTitle')
                    }}</span>
                    <span class="bd-body-regular">{{
                      tFeedbackPrompt('softPromptBody')
                    }}</span>
                  </div>
                </div>
                <bd-assistant-feedback-form
                  [(comment)]="feedbackComment"
                  mode="soft"
                  [interactive]="true"
                  [showSkip]="true"
                  (submitted)="onSoftPromptSubmitted($event)"
                  (skipped)="onSoftPromptSkipped()"
                ></bd-assistant-feedback-form>
              </div>
            }
          </ng-template>

          <!-- Pinned outside the scroller: an error after a long partial answer
        would otherwise render below the fold, and this is its only surface. -->
          @if (status() === 'error') {
            @if (visibleThinkingNote(); as note) {
              <div class="bd-pinned-status">
                <output
                  class="bd-chat-bubble-thinking bd-chat-bubble-thinking-error bd-body-regular"
                  aria-live="polite"
                  aria-atomic="true"
                  data-id="assistant-thinking-indicator"
                >
                  <i class="ui-info"></i>
                  @for (currentNote of [note]; track currentNote) {
                    <span class="bd-thinking-note-text">{{ currentNote }}</span>
                  }
                </output>
              </div>
            }
          }

          @if (
            suggestions().length > 0 &&
            status() === 'ready' &&
            !isWorkingThroughChain()
          ) {
            <div
              class="bd-pinned-suggestions flex flex-col gap-2 items-start"
              data-id="assistant-response-suggestions"
            >
              @for (suggestion of suggestions() | slice: 0 : 2; track $index) {
                <button
                  class="bd-chat-bubble bd-chat-bubble-suggestion bd-body-regular bd-suggestion-chip"
                  data-id="assistant-suggestion-chip"
                  (click)="onSuggestionClick(suggestion)"
                >
                  {{ suggestion | slice: 0 : 85 }}
                </button>
              }
            </div>
          }

          <!-- Chat Input -->
          <div class="bd-chat-container bd-small-scrollbar">
            <form
              (ngSubmit)="sendMessage(inputVal(), $event)"
              class="bd-chat-form w-full flex flex-col gap-1 rounded-lg"
            >
              @if (pendingAttachments().length > 0 || attachmentError()) {
                <div
                  #attachmentListContainer
                  class="flex flex-nowrap overflow-x-auto gap-2 px-2 pt-2 pb-1 bd-small-scrollbar"
                  data-id="assistant-attachment-list"
                >
                  @for (item of pendingAttachmentRow(); track $index) {
                    @if (item.kind === 'attachment') {
                      <bd-document-upload-chip
                        class="shrink-0"
                        [fileName]="
                          item.attachment.filename ?? item.attachment.mediaType
                        "
                        [fileType]="fileTypeLabel(item.attachment.mediaType)"
                        [fileSize]="fileSizeLabel(item.attachment.url)"
                        [iconClass]="fileTypeIcon(item.attachment.mediaType)"
                        state="uploaded"
                        errorMessage=""
                        data-id="assistant-attachment-chip-pending"
                        (remove)="removeAttachment(item.attachment)"
                      />
                    } @else {
                      <bd-document-upload-chip
                        class="shrink-0"
                        [fileName]="item.error.filename"
                        state="error"
                        [errorMessage]="
                          item.error.reason === 'size'
                            ? t('chat.attachmentTooLarge', {
                                max: item.error.maxMb,
                              })
                            : item.error.reason === 'conversion'
                              ? t('chat.attachmentReadFailed')
                              : item.error.reason === 'empty'
                                ? t('chat.attachmentEmpty', {
                                    filename: item.error.filename,
                                  })
                                : t('chat.attachmentUnsupported', {
                                    filename: item.error.filename,
                                  })
                        "
                        data-id="assistant-attachment-chip-error"
                        (remove)="attachmentError.set(null)"
                      />
                    }
                  }
                </div>
              }
              <textarea
                #chatInput
                name="message"
                rows="1"
                pTextarea
                (input)="autoResizeInput()"
                (keydown.enter)="sendMessage(inputVal(), $event)"
                [dt]="chatTextAreaPreset"
                [(ngModel)]="inputVal"
                data-id="assistant-chat-input"
                class="bd-chat-input w-full p-3 bd-body-regular"
                [placeholder]="t('chat.placeholder')"
              ></textarea>

              <div class="flex justify-end items-center m-2">
                @if (fileUploadEnabled()) {
                  <input
                    #fileInput
                    pInputText
                    type="file"
                    multiple
                    class="sr-only"
                    tabindex="-1"
                    [accept]="acceptedFileTypes"
                    [attr.aria-label]="t('chat.attachAriaLabel')"
                    (change)="onFilesSelected($event)"
                    data-id="assistant-attachment-input"
                  />
                  <p-button
                    class="mr-auto"
                    data-id="assistant-attach-button"
                    [text]="true"
                    [styleClass]="'bd-neutral bd-size-small'"
                    icon="ui-paper-clip"
                    [pTooltip]="t('chat.attachTooltip')"
                    tooltipPosition="right"
                    [attr.aria-label]="t('chat.attachAriaLabel')"
                    [disabled]="isProcessing() || isUploadingAttachments()"
                    (onClick)="onAttachClick()"
                  ></p-button>
                }
                <span
                  class="inline-flex"
                  [pTooltip]="isProcessing() ? t('chat.stopTooltip') : null"
                  tooltipPosition="left"
                  tooltipStyleClass="bd-ai-button-tooltip"
                >
                  <p-button
                    data-id="assistant-send-button"
                    [disabled]="
                      !isProcessing() &&
                      (!hasContent() ||
                        showSoftPrompt() ||
                        isConvertingAttachments() ||
                        isUploadingAttachments() ||
                        !!attachmentError())
                    "
                    [styleClass]="'bd-neutral bd-ai-submit bd-size-small'"
                    type="button"
                    [icon]="isProcessing() ? 'ui-rectangle' : 'ui-arrow-up'"
                    (onClick)="
                      isProcessing()
                        ? stopMessage()
                        : sendMessage(inputVal(), $event)
                    "
                  ></p-button>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </p-accordion-content>
  </p-accordion-panel>
</p-accordion>
